/**
 * The offline outbox: work done in a dead zone, replayed when the link returns.
 *
 * **Live vehicle commands are never queued, and that is the whole design.** An
 * ARM or TAKEOFF typed with no link and delivered twenty minutes later, to an
 * aircraft whose situation has completely changed, is how somebody gets hurt.
 * Commands fail immediately and say so. What queues here is *intent* that is
 * still true whenever it lands — a route somebody planned, a plan they renamed
 * — because a waypoint list means the same thing an hour later.
 *
 * Persisted to localStorage so closing the laptop in a dead zone does not throw
 * the work away, and keyed per operation so replaying twice cannot double-apply
 * anything. Order is preserved: two edits to the same plan must land in the
 * order they were made or the older one wins.
 */

import { useSyncExternalStore } from 'react';

import { setReachabilityObserver, type ApiResult } from '@/api/client';

const STORAGE_KEY = 'parallax.outbox.v1';
/** Give up on an entry that has failed this many times; it is not transient. */
const MAX_ATTEMPTS = 8;

export type OutboxKind = 'plan-save' | 'plan-rename';

export interface OutboxEntry {
  id: string;
  kind: OutboxKind;
  /** Everything the replay needs. Must stay JSON-serialisable. */
  payload: Record<string, unknown>;
  /** What the operator sees in the pending list. */
  label: string;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

export interface OutboxState {
  entries: OutboxEntry[];
  online: boolean;
  flushing: boolean;
  lastSyncAt: number | null;
}

type Sender = (entry: OutboxEntry) => Promise<ApiResult<unknown>>;

let state: OutboxState = {
  entries: load(),
  // navigator.onLine only knows about the network interface, never whether the
  // server is reachable. It seeds the state; a failed request is what actually
  // marks us offline.
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  flushing: false,
  lastSyncAt: null,
};

const listeners = new Set<() => void>();
const senders = new Map<OutboxKind, Sender>();

function load(): OutboxEntry[] {
  // localStorage THROWS in a locked-down webview or private mode — exactly the
  // environments an edge device runs in. Never let persistence break the app.
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as OutboxEntry[]) : [];
  } catch {
    return [];
  }
}

function persist(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.entries));
  } catch {
    /* out of quota or storage disabled — the queue still works in memory */
  }
}

function set(next: Partial<OutboxState>): void {
  state = { ...state, ...next };
  persist();
  for (const listener of listeners) listener();
}

/** Register how a kind of queued work is replayed. */
export function registerSender(kind: OutboxKind, sender: Sender): void {
  senders.set(kind, sender);
}

export function outboxSnapshot(): OutboxState {
  return state;
}

/**
 * Queue work for the next time the server is reachable.
 *
 * `dedupeKey` collapses repeated edits to the same thing: saving a route five
 * times offline should send the final version once, not replay four stale ones
 * over the top of it.
 */
export function enqueue(
  kind: OutboxKind,
  payload: Record<string, unknown>,
  label: string,
  dedupeKey?: string,
): void {
  const id = dedupeKey ? `${kind}:${dedupeKey}` : `${kind}:${Date.now()}:${Math.random()}`;
  const existing = state.entries.findIndex((entry) => entry.id === id);
  const entry: OutboxEntry = {
    id,
    kind,
    payload,
    label,
    createdAt: Date.now(),
    attempts: 0,
  };
  const entries = [...state.entries];
  if (existing >= 0) entries[existing] = entry;
  else entries.push(entry);
  set({ entries });
}

export function removeEntry(id: string): void {
  set({ entries: state.entries.filter((entry) => entry.id !== id) });
}

export function clearOutbox(): void {
  set({ entries: [] });
}

/** Record that a request reached the server, or did not. */
export function noteReachability(kind: string | undefined): void {
  const online = kind !== 'network' && kind !== 'timeout';
  if (online !== state.online) set({ online });
}

/**
 * Replay queued work, oldest first, stopping at the first network failure.
 *
 * Stopping matters: entries are ordered, and skipping past a failure to try a
 * later one would land two edits to the same plan out of order.
 */
export async function flush(): Promise<void> {
  if (state.flushing || state.entries.length === 0) return;
  set({ flushing: true });

  try {
    for (const entry of [...state.entries]) {
      const sender = senders.get(entry.kind);
      if (!sender) {
        // Nothing can replay it; dropping beats retrying forever.
        removeEntry(entry.id);
        continue;
      }
      const result = await sender(entry);
      if (result.ok) {
        removeEntry(entry.id);
        set({ lastSyncAt: Date.now(), online: true });
        continue;
      }

      if (result.error.kind === 'network' || result.error.kind === 'timeout') {
        set({ online: false });
        break; // still offline — keep the rest, in order, for next time
      }

      // The server answered and refused. Retrying an invalid payload forever
      // is a queue that never drains, so count attempts and give up loudly.
      const attempts = entry.attempts + 1;
      const entries = state.entries.map((e) =>
        e.id === entry.id ? { ...e, attempts, lastError: result.error.message } : e,
      );
      set({ entries, online: true });
      if (attempts >= MAX_ATTEMPTS) removeEntry(entry.id);
    }
  } finally {
    set({ flushing: false });
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useOutbox(): OutboxState {
  return useSyncExternalStore(subscribe, outboxSnapshot, outboxSnapshot);
}

/** Start watching for the link coming back. Called once, from the app shell. */
export function startOutbox(): () => void {
  if (typeof window === 'undefined') return () => {};
  // Every completed request tells us whether the server is reachable, which is
  // more honest than navigator.onLine — a radio can be up and still not route.
  setReachabilityObserver((kind) => {
    const wasOffline = !state.online;
    noteReachability(kind);
    if (wasOffline && state.online) void flush();
  });
  const onOnline = () => {
    set({ online: true });
    void flush();
  };
  const onOffline = () => set({ online: false });
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  // The browser's online event is not reliable behind a captive portal or a
  // radio that is up but not routing, so also retry on a slow timer.
  const timer = window.setInterval(() => void flush(), 15_000);
  void flush();
  return () => {
    setReachabilityObserver(null);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    window.clearInterval(timer);
  };
}
